<template>
  <Modal
    class="smart_modal_form"
    v-model:open="visible"
    :title="getModalTitle"
    :afterClose="closeModal"
    @ok="onOk"
    :width="modalWidth"
    :cancelText="lang(cancelText)"
    :okText="lang(okText)"
  >
    <Form
      :layout="props.fromLayout"
      :label-col="labelCol"
      :wrapper-col="wrapperCol"
      ref="formRef"
      :model="formState"
      :rules="formRules"
    >
      <template v-for="i in formOption.value" :key="i.prop">
        <FormItem :name="i.prop" :label="i.label + ':'">
          <component
            :key="formKey"
            :type="i.type"
            style="width: 100%"
            :is="getComponent(i.type)"
            v-model:value="formState[i.prop]"
            :placeholder="i.placeholder || lang('请输入')"
            :disabled="i.disabled"
            :dicData="i.dicData"
            :options="i.dicData"
            v-bind="i"
          />
        </FormItem>
      </template>
    </Form>
  </Modal>
</template>

<script lang="tsx" setup name="SmartModalForm">
import { computed, inject, onMounted, reactive, ref, watchEffect } from 'vue'
import { message, Modal, Form, FormItem } from 'ant-design-vue'
import { filterFormOption } from '../smart-crud/utils'
import { getComponent } from '../utils/tools'

const lang = inject('lang')

const emit = defineEmits(['success'])
const props = defineProps({
  option: {
    type: Object,
    required: true
  },
  fromLayout: {
    type: String,
    default: 'horizontal'
  },
  cancelText: {
    type: String,
    default: '取消'
  },
  okText: {
    type: String,
    default: '确定'
  }
})
const formKey = ref(Date.now())

const API = props?.option?.API || {}

const formRef = ref()
const visible = ref(false)
const modalType = ref('create')
const formState = ref<Record<any, any>>({})
const modalData = ref<Record<any, any>>({})
const labelCol = ref({ span: 24 })
const wrapperCol = ref({ span: 24 })
const modalWidth = ref('800px')

if (props.option.modalWidth) {
  modalWidth.value = props.option.modalWidth
}

const formOption = reactive<{ value: Record<any, any>[] }>({ value: [] })

const formRules = reactive<Record<any, any>>({})

watchEffect(() => {
  const sfo = filterFormOption(props.option.column, lang) as Record<any, any>[]
  // 每次option发生变化重置表单
  formKey.value = Date.now()
  if (modalType.value === 'update') {
    formOption.value = sfo
      .filter((i: Record<any, any>) => i.editDisplay !== false)
      .map((i) => ({ disabled: i.editDisabled, ...i })) as Record<any, any>[]
  }
  if (modalType.value === 'create') {
    formOption.value = sfo
      .filter((i: Record<any, any>) => i.addDisplay !== false)
      .map((i) => ({ disabled: i.addDisabled, ...i })) as Record<any, any>[]
  }
})

const init = () => {
  const sfo = filterFormOption(props.option.column, lang) as Record<any, any>[]

  sfo.forEach((i) => {
    if (i.rules && i.rules.length > 0) {
      formRules[i.prop] = i.rules
    }
    if (i.valueDefault !== undefined) {
      formState.value[i.prop] = i.valueDefault
    }
  })
}

onMounted(() => {
  init()
})

const showModal = (type = 'create', record = {}) => {
  modalType.value = type
  visible.value = true

  modalData.value = Object.assign({}, record)
  formState.value = Object.assign({}, record)
}
const closeModal = () => {
  visible.value = false
  modalData.value = {}
  formState.value = {}
  formRef.value.resetFields()
}

const createTeam = async (values: Record<any, any>) => {
  const params = props.option.setAddParams ? props.option.setAddParams(values) : values
  const data = await API.create(params)
  if (data.code === 200 || data.code === 0) {
    message.success(lang('创建成功'))
    closeModal()
    emit('success')
  } else {
    message.error(data.msg || lang('系统异常'))
  }
}

const updateTeam = async (values: Record<any, any>) => {
  const editValue = { id: modalData.value.id, ...values }
  const params = props.option.setAddParams ? props.option.setAddParams(editValue) : editValue
  const data = await API.update(params)
  if (data.code === 200 || data.code === 0) {
    message.success(lang('修改成功'))
    closeModal()
    emit('success')
  } else {
    message.error(data.msg || lang('系统异常'))
  }
}

const onOk = () => {
  formRef.value.validateFields().then((values: any) => {
    // 新增或编辑前做一些拦截操作
    if (typeof props.option.beforeAdd === 'function') {
      const state = props.option.beforeAdd(values)
      if (!state) return
    }
    if (modalType.value === 'create') {
      createTeam(values)
    } else {
      updateTeam(values)
    }
  })
}

const getModalTitle = computed(() => {
  if (modalType.value === 'create') {
    return '新增'
  }
  if (modalType.value === 'update') {
    return '修改'
  }
  return ''
})

defineExpose({
  showModal
})
</script>
<style lang="less">
.smart_modal_form {
  .ant-modal-content {
    padding: 0;
    .ant-modal-close {
      top: 12px;
    }
  }
  .ant-modal-header {
    border-bottom: 1px #e4e4e4 solid;
    padding: 12px 24px;
  }
  .ant-modal-body {
    padding: 0 24px;
  }
  .ant-modal-footer {
    padding: 0 24px 12px;
  }
}
</style>
